/* eslint-disable */
import { MutableRefObject, useRef, useState } from 'react';
import VariableNameAnswers from '../context/types/VariableNameAnswers';
import { justAnswersFromProcedureRecord } from '../context/util/recordUtils';
import { createNotificationsEngine, createRulesEngine, runEvaluationSequence } from '../util/procedureRulesUtils';
import { EvaluateRulesArgs, HandleAlertTriggeredFn, RuleEffects, UseProcedureRulesProps, UseProcedureRulesReturn } from './useProcedureRules.types';
import { accumulate } from '../util/mutationUtils';
import { RecordAlerts, Rule } from '../types';
import { parseAndExtractVariableNamesFromCondition, prependVarToAnswersForRules } from '../util/logic/ProcedureLogicUtil';
import { AlertMessage } from '../types/RecordAlerts';

/**
 * Custom hook for managing procedure rule evaluation within a React component.
 * It handles the lifecycle of rule execution, including:
 * - Initializing the rules engine.
 * - Tracking the processing state of rules.
 * - Accumulating effects from rule evaluations (alerts, auto-queries, variable enable/disable states).
 * - Providing a function to trigger rule evaluations and retrieve their outcomes.
 *
 * @public
 * @param props - The procedure ID and rule set
 * @returns An interface for interacting with rule evaluation and its state
 */
export function useProcedureRules({
  rules,
  procedureId,
}: UseProcedureRulesProps): UseProcedureRulesReturn {
  /**
   * Tracks whether rules are actively being processed.
   * Useful for controlling UI state during evaluation.
   *
   * @public
   */
  const [rulesProcessing, setRulesProcessing] = useState(false);

  /**
   * Accumulates rule-triggered effects (alerts, auto-queries, variable toggles).
   * Used internally during a rule evaluation cycle.
   *
   * @internal
   */
  const currentRuleEffects: MutableRefObject<RuleEffects> = useRef<RuleEffects>({ // TypeScript type hint retained for editor support
    alerts: [],
    notificationAlerts: [],
    autoQueries: [],
    variablesToEnable: {},
    variablesToDisable: {},
  });

  // --- Internal Functions ---
  // These functions are typically passed as callbacks to the rules engine
  // and are responsible for accumulating effects into `currentRuleEffects.current`.

  /**
   * Accumulates variable names to be enabled for a specific record.
   * Modifies `currentRuleEffects.current.variablesToEnable`.
   *
   * @internal
   * @param names - An array of variable names to enable.
   * @param recordId - The ID of the record to which these enable actions apply.
   */
  const doQuestionEnableAccumulator = (names: string[], recordId: string): void =>
    accumulate(
      currentRuleEffects.current.variablesToEnable,
      names,
      recordId
    );

  /**
   * Accumulates variable names to be disabled for a specific record.
   * Modifies `currentRuleEffects.current.variablesToDisable`.
   *
   * @internal
   * @param names - An array of variable names to disable.
   * @param recordId - The ID of the record to which these disable actions apply.
   */
  const doQuestionDisableAccumulator = (names: string[], recordId: string): void =>
    accumulate(
      currentRuleEffects.current.variablesToDisable,
      names,
      recordId
    );


  /**
   * Accumulates alert and auto-query messages triggered by a rule.
   *
   * @internal
   * @param alertText - Message to show as an alert (optional)
   * @param autoQueryText - Message to show as an auto-query (optional)
   * @param recordId - Record or procedure context tied to the trigger
   * @param variableNames - Variables involved in triggering the alert/query
   */
  const handleAlertTriggeredAccumulator: HandleAlertTriggeredFn = (alertText, autoQueryText, recordId, variableNames) => {
    if (alertText) {
        currentRuleEffects.current.alerts.push(alertText);
    }
    if (autoQueryText) {
      currentRuleEffects.current.autoQueries.push({
        autoQueryText,
        procedureOrRecordId: recordId,
        variableNames,
      });
    }
  };

  /**
   * Creates an AlertMessage from a rule's condition and text.
   *
   * @param rule - Rule containing the condition that triggered the alert.
   * @param alertText - Text message for this alert.
   * @returns An AlertMessage with the provided text and extracted questions.
   */
  const ruleConditionToQuestionRecordPair = (rule: Rule, alertText:string): AlertMessage => {
    const triggeringQuestions = parseAndExtractVariableNamesFromCondition(rule.condition);
    return { message: alertText, triggeringQuestions};
  }

  /**
   * Accumulates notification alerts for a given rule and record.
   * If no entry exists for the recordId, create one, then append the alert.
   *
   * @param rule - The Rule object that triggered the alert.
   * @param recordId - The identifier of the record for which the alert is generated.
   */
  const handleNotificationAlertTriggeredAccumulator = (rule: Rule, recordId: string): void => {
    if (!rule) return

    const notifications: RecordAlerts[] = currentRuleEffects.current.notificationAlerts
    // Try to find an existing entry for this recordId.
    let entry: RecordAlerts | undefined = notifications.find(({ recordId: id }) => id === recordId)

    // If no entry exists, create one and push it to the array.
    if (!entry) {
      entry = { recordId, alerts: [] }
      notifications.push(entry)
    }

    // Convert the rule into the question record pair and append it to the alerts array.
    entry.alerts.push(
      ruleConditionToQuestionRecordPair(rule, rule.alertText ?? "")
    )
  }


  // --- Functions returned by the hook (public API) ---
  /**
   * Retrieves or creates an initialized rules engine instance for a specific record context.
   * The engine is configured with the provided rules and internal accumulator functions
   * to capture effects.
   *
   * @public
   * @param recordId - The identifier for the specific record context. This can be
   * the main `procedureId` or a more specific record ID within a larger procedure.
   * @returns An initialized rules engine instance.
   */
  const getRulesEngine = (recordId: string): any =>
    // Call the utility, passing hook functions as callbacks
    createRulesEngine({
      rules: rules || [], // Ensure array
      procedureId,
      recordId,
      onAlertTriggered: handleAlertTriggeredAccumulator,
      onEnableTriggered: doQuestionEnableAccumulator, // Pass hook's own function
      onDisableTriggered: doQuestionDisableAccumulator, // Pass hook's own function
    });



  const getNotificationsEngine = (recordId: string | null): any => {
    return createNotificationsEngine({
      rules: rules || [], // Ensure array
      procedureId,
      recordId: recordId || '',
      notificationHandler: handleNotificationAlertTriggeredAccumulator,
    });
  }

  /**
   * Runs rule evaluation and returns all accumulated effects.
   * Automatically handles state updates around the async operation.
   *
   * @public
   * @param recordAfterCalc - Record after calculations have been applied.  Used as the input state for rule evaluation.
   * @param changedVars - Set of variable names that triggered re-evaluation. Rules are only evaluated for these changes.
   * @param rulesEngine - Pre-created rules engine instance scoped to the record.
   * @param selectionAnswers - Optional selection values that may affect rule conditions.
   * @param isPrepopulated - If true, evaluation was triggered by defaulted values. Affects internal logic for skipping some rules.
   *
   * @returns A Promise resolving to accumulated rule effects or `null` on error.
   */
  const evaluateRulesAndGetEffects = async ({
    recordAfterCalc,
    changedVars,
    rulesEngine,
    notificationsEngine,
    selectionAnswers,
    isPrepopulated = false,
    isOnlyNotificationRun = false,
  }: EvaluateRulesArgs): Promise<RuleEffects | null> => {
    setRulesProcessing(true);
    // Extract answers map from the *final* state after calculations
    const answersForRules: VariableNameAnswers = prependVarToAnswersForRules(justAnswersFromProcedureRecord(recordAfterCalc));
    // Selection answers map is now directly passed in
    const selectionAnswersForRules = selectionAnswers && prependVarToAnswersForRules(selectionAnswers);
    let accumulatedEffects: RuleEffects | null = null;

    try {
      await runEvaluationSequence({
        engine: rulesEngine,
        notificationsEngine: notificationsEngine,
        justAnswers: answersForRules,
        variableNames: Array.from(changedVars), // Pass the specific variables that changed
        procedureId: procedureId, // Pass global procedureId context
        selectionAnswers: selectionAnswersForRules,
        isPrepopulated: isPrepopulated,
        isOnlyNotificationRun: isOnlyNotificationRun,
        effectsCollectorRef: currentRuleEffects, // Pass the hook's internal ref
      });

      // Effects are now in currentRuleEffects.current - create a copy
      accumulatedEffects = {
        alerts: [...currentRuleEffects.current.alerts],
        autoQueries: [...currentRuleEffects.current.autoQueries],
        notificationAlerts: [...currentRuleEffects.current.notificationAlerts],
        // Deep copy the Records to avoid external mutation issues
        variablesToEnable: { ...currentRuleEffects.current.variablesToEnable },
        variablesToDisable: { ...currentRuleEffects.current.variablesToDisable },
      };

      //TODO: Run second evaluation sequence here

    } catch (error) {
      console.error("evaluateRulesAndGetEffects caught error from rule evaluation sequence:", error);
      accumulatedEffects = null; // Indicate failure
    } finally {
      setRulesProcessing(false); // Set processing false after attempt
    }

    return accumulatedEffects; // Return the collected effects (or null on error)
  }

  return {
    rulesProcessing,
    setRulesProcessing,
    getRulesEngine,
    currentRuleEffects,
    evaluateRulesAndGetEffects,
    getNotificationsEngine,
  };
}
